import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import axios from '../utils/axios';

const ResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, otp } = location.state || {};
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!email || !otp) {
      navigate('/forgot-password');
    }
  }, [email, otp, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post('/api/auth/reset-password', { 
        email,
        otp,
        new_password: password 
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!email || !otp) return null;

  return (
    <div className="split-layout">
      <div className="split-layout-left">
        <div className="branding">
          <div className="branding-logo">
            <h1>MavScope</h1>
            <p>AI-Powered Roof & Siding Measurements</p>
          </div>
          
          <div className="branding-features">
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Create New Password</h3>
                <p>Choose a strong password with at least 8 characters.</p>
              </div>
            </div>
            
            <div className="branding-feature">
              <div className="branding-feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <div className="branding-feature-text">
                <h3>Secure Storage</h3>
                <p>Your password is encrypted and never stored in plain text.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="split-layout-right">
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <div className="mobile-logo">
            <h1>MavScope</h1>
            <p>Reset Password</p>
          </div>
          
          <div className="form-card">
            {success ? (
              <>
                <div className="form-header">
                  <div style={{ 
                    width: '64px', 
                    height: '64px', 
                    borderRadius: '50%', 
                    backgroundColor: 'var(--color-success-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto var(--spacing-4)'
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2" width="32" height="32">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h2 className="form-title">Password Reset Complete</h2>
                  <p className="form-description">
                    Your password has been successfully reset. You can now sign in with your new password.
                  </p>
                </div>
                
                <div className="form-actions">
                  <Link to="/login" style={{ width: '100%' }}>
                    <Button variant="primary" size="lg" fullWidth>
                      Sign In
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="form-header">
                  <h2 className="form-title">Reset your password</h2>
                  <p className="form-description">
                    Enter your new password below. Make sure it's at least 8 characters.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-fields">
                    <Input
                      label="New Password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      autoComplete="new-password"
                      autoFocus
                    />

                    <Input
                      label="Confirm Password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      required
                      autoComplete="new-password"
                    />

                    {error && <Alert variant="error">{error}</Alert>}
                  </div>

                  <div className="form-actions">
                    <Button 
                      type="submit" 
                      variant="primary"
                      size="lg"
                      fullWidth
                      isLoading={isLoading}
                    >
                      Reset Password
                    </Button>
                  </div>
                </form>

                <div className="form-footer">
                  <p>
                    <Link to="/login" className="link">Back to Sign In</Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
